import { Button, Label, SegmentedButtons, Tooltip, token } from "@may-i/mash-design-system";
import { PageSpinner } from "~/shared/ui/PageSpinner";
import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import type { BranchVersion } from "~/entities/branch-version/types";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { useBranchVersions } from "~/features/branch-version/hooks/use-branch-versions";
import { useUpdateBranchVersionActive } from "~/features/branch-version/hooks/use-branch-version-mutations";
import { formatDate, formatValidDate } from "~/shared/lib/format";
import { useToast } from "~/shared/ui/toast";
import { AddVersionDrawer } from "./AddVersionDrawer";
import { EditVersionDrawer } from "./EditVersionDrawer";

// --- Types ---

type UsageStatus = "inUse" | "unused" | "used";
type FilterType = "all" | "active" | "inactive";
type SortKey = "name" | "usageStatus" | "createdAt" | "validPeriod";
type SortOrder = "asc" | "desc";

const FAR_PAST_DATE = "1970-01-01";
const FAR_FUTURE_DATE = "2100-12-31";

function getUsageStatus(version: BranchVersion): UsageStatus {
  if (version.isActive) return "inUse";
  // 7차 be: 활성 토글 누른 적 없으면 무조건 미사용 (PO 기준)
  if (!version.hasBeenActive) return "unused";
  // 7차 be 후속: 사용된 적 있어도 validFrom 이 미래면 미사용 (예약 시작 시점이 아직 안 옴)
  if (version.validFrom) {
    const start = new Date(version.validFrom);
    start.setHours(0, 0, 0, 0);
    if (start > new Date()) return "unused";
  }
  return "used";
}

function areDateRangesOverlapping(
  a: { start: string; end: string },
  b: { start: string; end: string },
): boolean {
  const aStart = new Date(a.start).getTime();
  const aEnd = new Date(a.end).getTime();
  const bStart = new Date(b.start).getTime();
  const bEnd = new Date(b.end).getTime();
  return aStart <= bEnd && aEnd >= bStart;
}

function findOverlappingActiveVersion(
  target: BranchVersion,
  versions: BranchVersion[],
): BranchVersion | undefined {
  return versions
    .filter((v) => v.isActive && v.id !== target.id)
    .find((v) =>
      areDateRangesOverlapping(
        {
          start: v.validFrom ?? FAR_PAST_DATE,
          end: v.validTo ?? FAR_FUTURE_DATE,
        },
        {
          start: target.validFrom ?? FAR_PAST_DATE,
          end: target.validTo ?? FAR_FUTURE_DATE,
        },
      ),
    );
}

const USAGE_STATUS_PRIORITY: Record<UsageStatus, number> = {
  inUse: 0,
  unused: 1,
  used: 2,
};

const FILTER_MENU: FilterType[] = ["all", "active", "inactive"];

// --- Component ---

export function VersionManagementPage() {
  const { t } = useTranslation("version");
  const { branchUid } = useParams();
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.isStaff ?? false;
  const { data: versions = [], isPending: isVersionsPending } = useBranchVersions(branchUid);
  const updateActive = useUpdateBranchVersionActive(branchUid);
  const { showToast } = useToast();

  // 1-2: 필터 UI 는 staff 에게만 노출. 고객사일 땐 filteredAndSorted 가 강제로 isActive=true 만 통과시키므로 default 는 "all" 로 단순화 (currentUser 로딩 race 회피)
  const [filter, setFilter] = useState<FilterType>("all");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<BranchVersion | null>(null);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openEditDrawer = useCallback((version: BranchVersion) => setEditingVersion(version), []);
  const closeEditDrawer = useCallback(() => setEditingVersion(null), []);

  const handleActivate = useCallback(async (version: BranchVersion) => {
    if (version.hasBeenActive) {
      const ok = window.confirm(
        `${t("action.activateConfirm.title")}\n\n${t("action.activateConfirm.message")}`
      );
      if (!ok) return;
    }
    try {
      await updateActive.mutateAsync({ branchVersionId: version.id, isActive: true });
      showToast({ type: "success", title: t("action.useSuccess"), content: "" });
    } catch {
      showToast({ type: "error", title: t("action.error"), content: "" });
    }
  }, [updateActive, showToast, t]);

  const handleDeactivate = useCallback(async (version: BranchVersion) => {
    try {
      await updateActive.mutateAsync({ branchVersionId: version.id, isActive: false });
      showToast({ type: "success", title: t("action.endSuccess"), content: "" });
    } catch {
      showToast({ type: "error", title: t("action.error"), content: "" });
    }
  }, [updateActive, showToast, t]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder(key === "createdAt" ? "desc" : "asc");
    }
  };

  const latestVersionId = versions.length > 0 ? versions[0].id : null;

  const filteredAndSorted = useMemo(() => {
    let result = versions;

    // 1-2: 고객사는 filter state 와 무관하게 isActive=true 만
    if (!isStaff) {
      result = result.filter((v) => v.isActive);
    } else if (filter === "active") {
      result = result.filter((v) => v.isActive);
    } else if (filter === "inactive") {
      result = result.filter((v) => !v.isActive);
    }

    return [...result].sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;

      switch (sortKey) {
        case "name":
          return dir * a.name.localeCompare(b.name);
        case "usageStatus":
          return (
            dir *
            (USAGE_STATUS_PRIORITY[getUsageStatus(a)] -
              USAGE_STATUS_PRIORITY[getUsageStatus(b)])
          );
        case "createdAt":
          return dir * (a.createdAt - b.createdAt);
        case "validPeriod": {
          const aFrom = a.validFrom ? new Date(a.validFrom).getTime() : 0;
          const bFrom = b.validFrom ? new Date(b.validFrom).getTime() : 0;
          return dir * (aFrom - bFrom);
        }
        default:
          return 0;
      }
    });
  }, [versions, filter, sortKey, sortOrder, isStaff]);

  if (isVersionsPending) {
    return <Container><PageSpinner /></Container>;
  }

  return (
    <Container>
      <TitleRow>
        <Title>{t("title")}</Title>
        <Label variant="soft" color="default" size="xSmall">
          {versions.length}
        </Label>
      </TitleRow>

      <FilterRow>
        {isStaff ? (
          <SegmentedButtons
            size="small"
            variant="outlined"
            color="primary"
            menu={FILTER_MENU.map((f) => ({ value: f, label: t(`filter.${f}`) }))}
            value={filter}
            onValueChange={(value) => { if (value) setFilter(value as FilterType); }}
          />
        ) : (
          // 1-2: 고객사는 필터 UI 미노출 (사용 중만 노출되므로 무의미)
          <FilterPlaceholder />
        )}
        {isStaff && (
          // 1-1: 고객사는 버전 추가 미노출
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={openDrawer}
          >
            {t("addButton")}
          </Button>
        )}
      </FilterRow>

      <TableContainer>
        <HeadRow>
          <SortableHeadCell $flex onClick={() => handleSort("name")}>
            {t("table.name")}
            <SortIcon sortKey="name" activeSortKey={sortKey} sortOrder={sortOrder} />
          </SortableHeadCell>
          <HeadCell $flex>{t("table.description")}</HeadCell>
          <SortableHeadCell $width={100} onClick={() => handleSort("usageStatus")}>
            {t("table.usageStatus")}
            <SortIcon sortKey="usageStatus" activeSortKey={sortKey} sortOrder={sortOrder} />
          </SortableHeadCell>
          <SortableHeadCell $width={160} onClick={() => handleSort("createdAt")}>
            {t("table.createdAt")}
            <SortIcon sortKey="createdAt" activeSortKey={sortKey} sortOrder={sortOrder} />
          </SortableHeadCell>
          <SortableHeadCell $width={200} onClick={() => handleSort("validPeriod")}>
            {t("table.validPeriod")}
            <SortIcon sortKey="validPeriod" activeSortKey={sortKey} sortOrder={sortOrder} />
          </SortableHeadCell>
          {isStaff && <HeadCell $width={54} />}
          {isStaff && <HeadCell $width={54} />}
        </HeadRow>

        <TableBody>
          {filteredAndSorted.length > 0 ? (
            filteredAndSorted.map((version) => (
              <VersionRow
                isMutating={updateActive.isPending}
                key={version.id}
                version={version}
                versions={versions}
                isLatest={version.id === latestVersionId}
                isStaff={isStaff}
                t={t}
                onEdit={openEditDrawer}
                onActivate={handleActivate}
                onDeactivate={handleDeactivate}
              />
            ))
          ) : (
            <EmptyRow>{t("empty")}</EmptyRow>
          )}
        </TableBody>
      </TableContainer>

      <AddVersionDrawer
        open={drawerOpen}
        versions={versions}
        onClose={closeDrawer}
      />
      <EditVersionDrawer
        open={editingVersion !== null}
        version={editingVersion}
        onClose={closeEditDrawer}
      />
    </Container>
  );
}

// --- Sort Icons (inline SVG from Figma) ---

function ActiveSortIcon({ order }: { order: SortOrder }) {
  return (
    <SortIconBox>
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        style={order === "asc" ? { transform: "rotate(180deg)" } : undefined}
      >
        <path
          d="M9.38128 12.8813C9.72299 13.223 10.277 13.223 10.6187 12.8813L14.3396 9.16039C14.8908 8.60917 14.5004 7.66667 13.7209 7.66667H6.2791C5.49956 7.66667 5.10916 8.60917 5.66038 9.16039L9.38128 12.8813Z"
          fill="currentColor"
        />
      </svg>
    </SortIconBox>
  );
}

function DefaultSortIcon() {
  return (
    <SortIconBox>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M10.4125 5.16249C10.1847 4.93468 9.81536 4.93468 9.58756 5.16249L6.91252 7.83753C6.54504 8.20501 6.8053 8.83334 7.325 8.83334L12.6751 8.83334C13.1948 8.83334 13.455 8.20501 13.0876 7.83753L10.4125 5.16249Z"
          fill="currentColor"
        />
        <path
          d="M9.58755 14.8375C9.81535 15.0653 10.1847 15.0653 10.4125 14.8375L13.0875 12.1625C13.455 11.795 13.1948 11.1667 12.6751 11.1667H7.32498C6.80529 11.1667 6.54502 11.795 6.9125 12.1625L9.58755 14.8375Z"
          fill="currentColor"
        />
      </svg>
    </SortIconBox>
  );
}

function SortIcon({
  sortKey,
  activeSortKey,
  sortOrder,
}: {
  sortKey: SortKey;
  activeSortKey: SortKey;
  sortOrder: SortOrder;
}) {
  const isActive = sortKey === activeSortKey;

  if (isActive) {
    return <ActiveSortIcon order={sortOrder} />;
  }

  return (
    <HoverSortIcon>
      <DefaultSortIcon />
    </HoverSortIcon>
  );
}

// --- Version Row ---

function VersionRow({
  version,
  versions,
  isLatest,
  isMutating,
  isStaff,
  t,
  onEdit,
  onActivate,
  onDeactivate,
}: {
  version: BranchVersion;
  versions: BranchVersion[];
  isLatest: boolean;
  isMutating: boolean;
  isStaff: boolean;
  t: (key: string, opts?: Record<string, string>) => string;
  onEdit: (version: BranchVersion) => void;
  onActivate: (version: BranchVersion) => void;
  onDeactivate: (version: BranchVersion) => void;
}) {
  const status = getUsageStatus(version);

  const formatValidPeriod = () => {
    if (!version.validFrom && !version.validTo) return "-";
    const from = version.validFrom ? formatValidDate(version.validFrom) : "";
    const to = version.validTo ? formatValidDate(version.validTo) : "";
    if (from && to) return from === to ? from : `${from} - ${to}`;
    if (from) return `${from} -`;
    return to || "-";
  };

  const overlapping = status !== "inUse" ? findOverlappingActiveVersion(version, versions) : undefined;
  const hasNoDate = !version.validFrom && !version.validTo;

  const useButtonDisabledReason = (() => {
    if (status === "inUse") return null;
    if (hasNoDate) return t("action.noDateTooltip");
    if (overlapping) return t("action.overlapTooltip", { name: overlapping.name });
    return null;
  })();

  return (
    <BodyRow>
      <BodyCell $flex>
        <NameContent>
          {version.name}
          {isLatest && (
            <Label variant="soft" color="info" size="xSmall">
              {t("badge.latest")}
            </Label>
          )}
        </NameContent>
      </BodyCell>
      <BodyCell $flex>
        <DescriptionText>{version.description || "-"}</DescriptionText>
      </BodyCell>
      <BodyCell $width={100}>
        <Label
          variant="soft"
          color={status === "inUse" ? "success" : "default"}
          size="small"
        >
          {t(`status.${status}`)}
        </Label>
      </BodyCell>
      <BodyCell $width={160}>
        {formatDate(version.createdAt, "YYYY.MM.DD HH:mm")}
      </BodyCell>
      <BodyCell $width={200}>{formatValidPeriod()}</BodyCell>
      {isStaff && (
        <BodyCell $width={54}>
          <Button
            variant="outlined"
            color="primary"
            size="xSmall"
            disabled={isMutating}
            onClick={() => onEdit(version)}
          >
            {t("action.edit")}
          </Button>
        </BodyCell>
      )}
      {isStaff && (
        <BodyCell $width={54}>
          {status === "inUse" ? (
            <Button
              variant="outlined"
              color="primary"
              size="xSmall"
              disabled={isMutating}
              onClick={() => onDeactivate(version)}
            >
              {t("action.end")}
            </Button>
          ) : useButtonDisabledReason ? (
            <Tooltip
              trigger={
                <DisabledButtonWrapper>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="xSmall"
                    disabled
                  >
                    {t("action.use")}
                  </Button>
                </DisabledButtonWrapper>
              }
              side="top"
              align="end"
              withArrow
            >
              <TooltipText>{useButtonDisabledReason}</TooltipText>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              size="xSmall"
              disabled={isMutating}
              onClick={() => onActivate(version)}
            >
              {t("action.use")}
            </Button>
          )}
        </BodyCell>
      )}
    </BodyRow>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
`;

// 1-2: 고객사 view 에서 좌측 SegmentedButtons 자리를 비워두기 위한 placeholder (FilterRow 의 space-between 유지)
const FilterPlaceholder = styled.div``;


const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: ${token("colors.gray.white")};
  border-bottom: 1px solid ${token("colors.border.divider")};
`;

const HeadCell = styled.div<{ $width?: number; $flex?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  flex: ${({ $width, $flex }) =>
    $flex ? "1 1 0%" : $width ? `0 0 ${$width}px` : "0 0 auto"};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  min-height: 40px;
`;

const SortableHeadCell = styled(HeadCell)`
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: ${token("colors.action.hover")};
  }

  &:hover ${() => HoverSortIcon} {
    opacity: 1;
  }
`;

const SortIconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${token("colors.gray.GR900")};
`;

const HoverSortIcon = styled.span`
  display: inline-flex;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const BodyRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${token("colors.border.divider")};
`;

const BodyCell = styled.div<{ $width?: number; $flex?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 7px 8px;
  flex: ${({ $width, $flex }) =>
    $flex ? "1 1 0%" : $width ? `0 0 ${$width}px` : "0 0 auto"};
  min-height: 46px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const NameContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DescriptionText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 12px;
  font-size: 14px;
  color: ${token("colors.text.secondary")};
`;

const DisabledButtonWrapper = styled.div`
  display: inline-flex;
`;

const TooltipText = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  white-space: pre-wrap;
  max-width: 240px;
`;
